/**
 * Investor-Portal — Segment OUTBOUND (#1148 v2.548.0).
 *
 * Standort-getrennte Deckungsbeitrags-Analyse (AT vs DE) auf Basis Odoo
 * Kostenstellenbuchungen (account.analytic.line, Outbound Handling-Fee).
 *
 * Akzeptanzkriterium: Σ Umsatz Jan 2026 = +87.653,99 € (Spot-Check verifiziert).
 *
 * Layout:
 *   - PeriodToggle (YTD / Quartal / Monat / Datum)
 *   - 4 KPI-Tiles (Umsatz / Aufwand / DB / DB-Marge)
 *   - AT/DE/Σ-Tabelle (DB-Komponenten)
 *   - Recharts Monats-Trend (DB AT/DE + Σ-Linie)
 *
 * Design strikt nach SKILL-design.md:
 *   Border-Cards, KEINE Shadows, KPI-Werte --text-primary (#1A1A2E), DM Sans.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { TrendingUp, Wallet, BarChart3, Percent } from 'lucide-react';
import PeriodToggle, { buildPeriodQuery } from './components/PeriodToggle';
import DataMaturityBanner from './components/DataMaturityBanner';
import { MaturityOverlay, reifeFooter } from './components/MaturityChartOverlay';
import { defaultMatureMonthState, applyYtdCutoff } from '../../utils/data-maturity';

const API = import.meta.env.VITE_API_URL || '';
const MONTH_LABELS = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

function fmtEur(v) {
  if (v === null || v === undefined || isNaN(v)) return '–';
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}
function fmtEurDetail(v) {
  if (v === null || v === undefined || isNaN(v)) return '–';
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v);
}
function fmtPct(v) {
  if (v === null || v === undefined || isNaN(v)) return '–';
  return `${v.toFixed(1)} %`;
}
function fmtEurCent(v) {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
}
function fmtInt(v) {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return new Intl.NumberFormat('de-AT', { maximumFractionDigits: 0 }).format(v);
}
function fmtDec(v, digits = 2) {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return new Intl.NumberFormat('de-AT', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v);
}

export default function InvestorSegmentOutbound() {
  const [searchParams] = useSearchParams();
  const currentYear = new Date().getFullYear();

  const [periodState, setPeriodState] = useState(() => {
    // #1184 v2.569.0 — Default = letzter belastbarer Monat (vorletzter Monat),
    // statt YTD. Manuel-Regel: erst nach 2 vollen Monaten ist Monat M reif.
    const mature = defaultMatureMonthState();
    const p = searchParams.get('period') || 'month';
    const y = parseInt(searchParams.get('year') || mature.year, 10);
    const m = parseInt(searchParams.get('month') || mature.month, 10);
    const q = parseInt(searchParams.get('quarter') || Math.ceil((new Date().getMonth() + 1) / 3), 10);
    return {
      period: p,
      year: y,
      month: m,
      quarter: q,
      fromDate: searchParams.get('from_date') || null,
      toDate: searchParams.get('to_date') || null,
    };
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // #1150 v2.551.0 — Chart-Quelle entkoppelt vom Period-Toggle.
  // Eigener State + Fetch nur abhängig von year. Chart speist sich exklusiv aus
  // yearTrend, KPI-Tiles + Tabelle + Reconciliation + Stück-KPIs weiter aus data.
  const [yearTrend, setYearTrend] = useState(null);
  const [yearTrendLoading, setYearTrendLoading] = useState(true);
  const [yearTrendError, setYearTrendError] = useState(null);

  // #1184 v2.569.0 — YTD wird frontend-seitig auf custom-Range mit Cutoff
  // bis zum letzten belastbaren Monat verkürzt (Buchungen laufen ~1 Monat nach).
  const effectivePeriodState = useMemo(() => applyYtdCutoff(periodState), [periodState]);
  const periodQuery = useMemo(() => buildPeriodQuery(effectivePeriodState), [effectivePeriodState]);

  useEffect(() => {
    let abort = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const q = new URLSearchParams(periodQuery);
        const res = await fetch(`${API}/api/investor/segmente/outbound?${q.toString()}`, { credentials: 'include' });
        const json = await res.json();
        if (abort) return;
        if (!res.ok) {
          setError(json.error || `HTTP ${res.status}`);
          setData(null);
        } else {
          setData(json);
        }
      } catch (e) {
        if (!abort) setError(e.message);
      } finally {
        if (!abort) setLoading(false);
      }
    }
    load();
    return () => { abort = true; };
  }, [periodQuery]);

  // #1150 — Jahres-Trend separat fetchen (period-unabhängig, nur year).
  useEffect(() => {
    let abort = false;
    async function load() {
      setYearTrendLoading(true);
      setYearTrendError(null);
      try {
        const res = await fetch(`${API}/api/investor/segmente/outbound/jahrestrend?year=${periodState.year}`, { credentials: 'include' });
        const json = await res.json();
        if (abort) return;
        if (!res.ok) {
          setYearTrendError(json.error || `HTTP ${res.status}`);
          setYearTrend(null);
        } else {
          setYearTrend(json);
        }
      } catch (e) {
        if (!abort) setYearTrendError(e.message);
      } finally {
        if (!abort) setYearTrendLoading(false);
      }
    }
    load();
    return () => { abort = true; };
  }, [periodState.year]);

  // KPIs aus GESAMT-Zeile ableiten
  const kpis = useMemo(() => {
    const gesamt = data?.standorte?.find(s => s.standort === 'GESAMT');
    if (!gesamt) return { umsatz: null, aufwand: null, db: null, marge: null };
    const aufwand = (gesamt.aufwand_lohn || 0) + (gesamt.aufwand_direkt || 0) + (gesamt.aufwand_revshare || 0);
    return {
      umsatz: gesamt.umsatz,
      aufwand,
      db: gesamt.db,
      marge: gesamt.db_marge_pct,
    };
  }, [data]);

  // Chart-Datenpunkte aus Jahres-Trend (immer 12 Monate, period-unabhängig — #1150).
  const chartData = useMemo(() => {
    if (!yearTrend?.monats_trend) return [];
    return yearTrend.monats_trend.map(m => ({
      monat: MONTH_LABELS[m.monat - 1],
      AT: Math.round(m.at_db || 0),
      DE: Math.round(m.de_db || 0),
      Gesamt: Math.round(m.gesamt_db || 0),
    }));
  }, [yearTrend]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A2E]">Segment Outbound</h1>
          <div className="text-sm text-[#6B7280] mt-1">
            Deckungsbeitrag standort-getrennt (AT vs DE) — Odoo-Kostenstelle „Outbound Handling-Fee"
          </div>
        </div>
        <PeriodToggle
          period={periodState.period}
          year={periodState.year}
          month={periodState.month}
          quarter={periodState.quarter}
          fromDate={periodState.fromDate}
          toDate={periodState.toDate}
          onChange={setPeriodState}
        />
      </div>

      {/* #1184 v2.569.0 — Datenreife-Banner (rot/amber/info je nach Zeitfilter) */}
      <DataMaturityBanner periodState={periodState} />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">
          Fehler: {error}
        </div>
      )}

      {/* KPI Tiles — farbige Icons (#1150 Teil C), Werte bleiben --text-primary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile
          label="Umsatz" value={fmtEur(kpis.umsatz)} sub="Kundenrechnungen Outbound" loading={loading}
          Icon={TrendingUp} iconColor="#338edc" iconTint="#EBF5FD"
        />
        <KpiTile
          label="Aufwand" value={fmtEur(kpis.aufwand)} sub="Lohn + Direkt + RevShare" loading={loading}
          Icon={Wallet} iconColor="#EF4444" iconTint="#FEE2E2"
        />
        <KpiTile
          label="Deckungsbeitrag" value={fmtEur(kpis.db)} sub="Umsatz − Aufwand" loading={loading}
          Icon={BarChart3} iconColor="#10B981" iconTint="#D1FAE5"
        />
        <KpiTile
          label="DB-Marge" value={fmtPct(kpis.marge)} sub="DB ÷ Umsatz" loading={loading}
          Icon={Percent} iconColor="#6366F1" iconTint="#E0E7FF"
        />
      </div>

      {/* Reconciliation-Indikator (#1149 Teil B) */}
      {data?.reconciliation && (
        <div className={`text-xs px-3 py-2 rounded-lg border flex items-center gap-2 ${
          data.reconciliation.ok === true
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : data.reconciliation.ok === false
            ? 'bg-amber-50 border-amber-200 text-amber-800'
            : 'bg-[#F8F9FC] border-[#E5E7EB] text-[#6B7280]'
        }`}>
          <span className={`w-2 h-2 rounded-full ${
            data.reconciliation.ok === true ? 'bg-emerald-500'
            : data.reconciliation.ok === false ? 'bg-amber-500'
            : 'bg-gray-400'
          }`} />
          {data.reconciliation.ok === true && (
            <span>GuV-Reconciliation OK — DE-RevShare ({fmtEurCent(data.reconciliation.actual)}) deckt sich mit GuV-Konto 5720 auf Outbound-Kostenstellen.</span>
          )}
          {data.reconciliation.ok === false && (
            <span>
              GuV-Reconciliation Abweichung: erwartet {fmtEurCent(data.reconciliation.expected)} · aktuell {fmtEurCent(data.reconciliation.actual)} · Δ {fmtEurCent(data.reconciliation.diff)}
            </span>
          )}
          {data.reconciliation.ok === null && (
            <span>Reconciliation nicht verfügbar{data.reconciliation.error ? ` (${data.reconciliation.error})` : ''}.</span>
          )}
        </div>
      )}

      {/* Standort-Tabelle */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[#1A1A2E]">Standort-Split</h2>
          {data && (
            <span className="text-[11px] text-[#9CA3AF]">
              {data.lines_processed} Odoo-Buchungen · {data.period?.dateFrom} – {data.period?.dateTo}
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-24">Standort</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Umsatz</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Lohn (Andere)</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Direkt</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">RevShare</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">DB</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-20">Marge</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-[#9CA3AF]">Lade Odoo-Daten…</td></tr>
              )}
              {!loading && data?.standorte?.map(s => (
                <tr
                  key={s.standort}
                  className={`border-t border-[#F3F4F6] ${s.standort === 'GESAMT' ? 'bg-[#F8F9FC] font-semibold' : 'hover:bg-[#FAFBFC]'}`}
                >
                  <td className="px-3 py-2 text-[#1A1A2E]">{labelFor(s.standort)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(s.umsatz)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtEurDetail(s.aufwand_lohn)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtEurDetail(s.aufwand_direkt)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtEurDetail(s.aufwand_revshare)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E] font-medium">{fmtEurDetail(s.db)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtPct(s.db_marge_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stück-KPIs (#1149 Teil C) */}
      {data?.stueck_kpis && !data.stueck_kpis.error && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl">
          <div className="px-4 py-3 border-b border-[#E5E7EB]">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Stück-Kosten Outbound</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Kostenbasis asymmetrisch — AT: Lohn + Direkt-Aufwand · DE: Finecom-RevShare.
              Mengen aus Billing-Order-Rechnungen.
            </div>
          </div>
          <div className="p-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StueckKpiTile
              label="Kosten / Paket AT"
              value={fmtEurCent(data.stueck_kpis.kosten_paket_at)}
              sub={`${fmtInt(data.mengen?.AT?.pakete)} Pakete · Basis ${fmtEur(data.stueck_kpis.cost_basis_at)}`}
            />
            <StueckKpiTile
              label="Kosten / Artikel AT"
              value={fmtEurCent(data.stueck_kpis.kosten_artikel_at)}
              sub={`${fmtInt(data.mengen?.AT?.picks)} Picks`}
            />
            <StueckKpiTile
              label="Kosten / Paket DE"
              value={fmtEurCent(data.stueck_kpis.kosten_paket_de)}
              sub={`${fmtInt(data.mengen?.DE?.pakete)} Pakete · Basis ${fmtEur(data.stueck_kpis.cost_basis_de)}`}
            />
            <StueckKpiTile
              label="Kosten / Artikel DE"
              value={fmtEurCent(data.stueck_kpis.kosten_artikel_de)}
              sub={`${fmtInt(data.mengen?.DE?.picks)} Picks`}
            />
          </div>
        </div>
      )}

      {/* AT/DE-Vergleich (#1168) — Struktur- + Erlös-KPIs erklären die Kostendifferenz */}
      {data?.vergleich_kpis && !data.vergleich_kpis.error && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl">
          <div className="px-4 py-3 border-b border-[#E5E7EB]">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">AT/DE-Vergleich Outbound</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Order-Struktur (Picks pro Paket) sowie Umsatz und Roherlös pro Paket — erklärt
              die Stückkosten-Differenz zwischen Standorten. Roherlös = Umsatz − Aufwand
              (AT: Lohn + Direkt · DE: RevShare).
            </div>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <VergleichPair
              label="Picks pro Paket"
              valueAt={fmtDec(data.vergleich_kpis.picks_per_paket_at, 2)}
              valueDe={fmtDec(data.vergleich_kpis.picks_per_paket_de, 2)}
              subAt={`${fmtInt(data.mengen?.AT?.picks)} Picks · ${fmtInt(data.mengen?.AT?.pakete)} Pakete`}
              subDe={`${fmtInt(data.mengen?.DE?.picks)} Picks · ${fmtInt(data.mengen?.DE?.pakete)} Pakete`}
            />
            <VergleichPair
              label="Umsatz pro Paket"
              valueAt={fmtEurCent(data.vergleich_kpis.umsatz_per_paket_at)}
              valueDe={fmtEurCent(data.vergleich_kpis.umsatz_per_paket_de)}
              subAt="Outbound-Umsatz ÷ Pakete"
              subDe="Outbound-Umsatz ÷ Pakete"
            />
            <VergleichPair
              label="Roherlös pro Paket"
              valueAt={fmtEurCent(data.vergleich_kpis.roherloes_per_paket_at)}
              valueDe={fmtEurCent(data.vergleich_kpis.roherloes_per_paket_de)}
              subAt={`Roherlös ${fmtEur(data.vergleich_kpis.roherloes_at)}`}
              subDe={`Roherlös ${fmtEur(data.vergleich_kpis.roherloes_de)}`}
            />
          </div>
          <div className="px-4 pb-3 text-[11px] text-[#9CA3AF]">
            Hinweis: WeShip-Odoo bucht Rechnungen ohne SKU-Auflösung — SKUs pro Order entspricht
            mathematisch den Picks pro Paket (Picks = Anzahl Artikelpositionen).
          </div>
        </div>
      )}

      {/* Verpackungs-Gewichtsklassen (#1168) — VVV-0001…VVV-0005 als Preis-/Gewichts-Tier */}
      {data?.verpackungsklassen?.klassen?.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl">
          <div className="px-4 py-3 border-b border-[#E5E7EB]">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Verpackungs-Gewichtsklassen</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Verteilung der berechneten Verpackungseinheiten („VVV") nach Preis-Tier — Proxy für
              Paket-Gewicht/-Größe. Quelle: Billing-Order-Rechnungen ({fmtInt(data.verpackungsklassen.total_at + data.verpackungsklassen.total_de)} Σ).
            </div>
          </div>
          <div className="p-4">
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data.verpackungsklassen.klassen.map(k => ({
                    klasse: `${k.code} · ${fmtEurCent(k.list_price)}`,
                    AT: Math.round(k.qty_at || 0),
                    DE: Math.round(k.qty_de || 0),
                  }))}
                  margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
                >
                  <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="klasse" tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#6B7280' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={v => new Intl.NumberFormat('de-AT', { notation: 'compact', maximumFractionDigits: 1 }).format(v)}
                  />
                  <Tooltip
                    formatter={v => fmtInt(v)}
                    labelStyle={{ color: '#1A1A2E', fontWeight: 600 }}
                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
                  <Bar dataKey="AT" fill="#338edc" />
                  <Bar dataKey="DE" fill="#22D3EE" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Klasse</th>
                    <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Preis-Tier</th>
                    <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Menge AT</th>
                    <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Menge DE</th>
                    <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Anteil AT</th>
                    <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Anteil DE</th>
                  </tr>
                </thead>
                <tbody>
                  {data.verpackungsklassen.klassen.map(k => (
                    <tr key={k.product_id} className="border-t border-[#F3F4F6]">
                      <td className="px-3 py-2 text-[#1A1A2E]">{k.code}</td>
                      <td className="px-3 py-2 text-[#4B5563]">{fmtEurCent(k.list_price)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtInt(k.qty_at)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtInt(k.qty_de)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#6B7280]">
                        {data.verpackungsklassen.total_at > 0 ? fmtPct((k.qty_at / data.verpackungsklassen.total_at) * 100) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#6B7280]">
                        {data.verpackungsklassen.total_de > 0 ? fmtPct((k.qty_de / data.verpackungsklassen.total_de) * 100) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Monats-Trend — IMMER 12 Monate des Jahres, period-unabhängig (#1150 Teil B) */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Monats-Trend Deckungsbeitrag {periodState.year}</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Voller Jahresverlauf Jan–Dez — unabhängig vom Periode-Toggle. AT (eigenes Personal), DE (Finecom-RevShare), Σ.
            </div>
          </div>
          {yearTrendError && (
            <span className="text-[11px] text-red-600">Fehler: {yearTrendError}</span>
          )}
        </div>
        <div className="p-4">
          {yearTrendLoading && !yearTrend && (
            <div className="h-[320px] flex items-center justify-center text-[#9CA3AF] text-sm">Lade Jahres-Trend…</div>
          )}
          {!yearTrendLoading && chartData.length === 0 && (
            <div className="h-[320px] flex items-center justify-center text-[#9CA3AF] text-sm">Keine Daten für {periodState.year}.</div>
          )}
          {chartData.length > 0 && reifeFooter(periodState.year) && (
            <div className="text-[11px] text-[#9CA3AF] mb-2">
              {reifeFooter(periodState.year)}
            </div>
          )}
          {chartData.length > 0 && (
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="monat" tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#6B7280' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={v => new Intl.NumberFormat('de-AT', { notation: 'compact', maximumFractionDigits: 1 }).format(v) + ' €'}
                  />
                  <Tooltip
                    formatter={v => fmtEurDetail(v)}
                    labelStyle={{ color: '#1A1A2E', fontWeight: 600 }}
                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
                  {/* #1184 v2.569.0 — Gelbe Markierung der vorläufigen Monate */}
                  {MaturityOverlay({ year: periodState.year, monthLabels: MONTH_LABELS })}
                  <Line type="monotone" dataKey="AT" stroke="#338edc" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="DE" stroke="#22D3EE" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="Gesamt" stroke="#6366F1" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="text-xs text-[#9CA3AF]">
        Quelle: Odoo <code>account.analytic.line</code> · Kostenstelle „Outbound Handling-Fee" (id=58) ·
        Standort-Zuordnung via <code>kunden.odoo_partner_id</code> → <code>kunden.standort_id</code> →
        <code> standorte</code>. Finecom-Lieferantenrechnungen werden als DE-RevShare klassifiziert
        (Partner-IDs konfigurierbar in <code>investor_segment_mapping</code>).
      </div>
    </div>
  );
}

function labelFor(standort) {
  if (standort === 'AT') return 'Österreich';
  if (standort === 'DE') return 'Deutschland';
  if (standort === 'GESAMT') return 'Gesamt';
  return standort;
}

function KpiTile({ label, value, sub, loading, Icon, iconColor, iconTint }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">{label}</div>
        {Icon && (
          <span
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
            style={{ backgroundColor: iconTint || '#F3F4F6' }}
            aria-hidden="true"
          >
            <Icon width={16} height={16} stroke={iconColor || '#6B7280'} strokeWidth={2} fill="none" />
          </span>
        )}
      </div>
      <div className="text-2xl font-semibold text-[#1A1A2E] tabular-nums">
        {loading ? <span className="inline-block w-24 h-7 rounded bg-[#F3F4F6] animate-pulse" /> : value}
      </div>
      {sub && <div className="text-[11px] text-[#9CA3AF] mt-1">{sub}</div>}
    </div>
  );
}

function StueckKpiTile({ label, value, sub }) {
  return (
    <div className="border border-[#E5E7EB] rounded-lg p-3 bg-[#FAFBFC]">
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">{label}</div>
      <div className="text-lg font-semibold text-[#1A1A2E] tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-[#9CA3AF] mt-1">{sub}</div>}
    </div>
  );
}

function VergleichPair({ label, valueAt, valueDe, subAt, subDe }) {
  return (
    <div className="border border-[#E5E7EB] rounded-lg p-3 bg-[#FAFBFC]">
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">{label}</div>
      <div className="grid grid-cols-2 gap-2">
        <div className="border-r border-[#E5E7EB] pr-2">
          <div className="text-[10px] uppercase text-[#338edc] font-semibold tracking-wider">AT</div>
          <div className="text-lg font-semibold text-[#1A1A2E] tabular-nums">{valueAt}</div>
          {subAt && <div className="text-[10px] text-[#9CA3AF] mt-0.5 leading-tight">{subAt}</div>}
        </div>
        <div className="pl-2">
          <div className="text-[10px] uppercase text-[#22D3EE] font-semibold tracking-wider">DE</div>
          <div className="text-lg font-semibold text-[#1A1A2E] tabular-nums">{valueDe}</div>
          {subDe && <div className="text-[10px] text-[#9CA3AF] mt-0.5 leading-tight">{subDe}</div>}
        </div>
      </div>
    </div>
  );
}
